import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  HardDrive, Cloud, Loader2, PlayCircle, Clock, CheckCircle2,
  AlertTriangle, ShieldCheck, Info, X, Download, DatabaseBackup,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_BADGE = {
  success:                "bg-[#39FF14] text-[#0B0F0C] border-[#39FF14]",
  failed:                 "bg-red-500/15 text-red-300 border-red-500/40",
  in_progress:            "bg-yellow-500/15 text-yellow-200 border-yellow-500/40",
  pending_cloud_upload:   "bg-yellow-500/15 text-yellow-200 border-yellow-500/40",
};

export default function Backups() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "co_admin";
  const [rows, setRows] = useState([]);
  const [settings, setSettings] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  // Offline-backup (pod-local, admin-only download) state. Isolated from the
  // normal cloud-upload flow above — this is the emergency path.
  const [offlineRows, setOfflineRows] = useState([]);
  const [offlineRunning, setOfflineRunning] = useState(false);
  const [offlineDownloading, setOfflineDownloading] = useState(null); // backup_id in flight

  const load = async () => {
    try {
      const [{ data: hist }, { data: cfg }] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/settings/current"),
      ]);
      setRows(hist || []);
      setSettings(cfg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load backups"); }
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  // -------- Offline (pod-local) backup helpers --------
  const loadOffline = async () => {
    try {
      const { data } = await api.get("/admin/maintenance/backup/list");
      setOfflineRows(data?.backups || []);
    } catch (e) {
      // Non-fatal: just show empty list.
      setOfflineRows([]);
    }
  };
  useEffect(() => { if (isAdmin) loadOffline(); }, [isAdmin]);

  const runOfflineBackup = async () => {
    if (!window.confirm("Create a pod-local mongodump snapshot now? This does NOT touch Google Drive.")) return;
    setOfflineRunning(true);
    try {
      const { data } = await api.post("/admin/maintenance/backup/run");
      if (data?.success) toast.success(`Offline backup created: ${data.backup_id}`);
      else toast.error("Backup script finished but reported failure — see server logs");
      await loadOffline();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create offline backup");
    } finally { setOfflineRunning(false); }
  };

  const downloadOffline = async (backup_id) => {
    setOfflineDownloading(backup_id);
    try {
      const res = await api.get(`/admin/maintenance/backup/download/${encodeURIComponent(backup_id)}`,
                                { responseType: "blob", timeout: 15 * 60 * 1000 });
      const blob = new Blob([res.data], { type: "application/gzip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${backup_id}.tar.gz`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
    } finally { setOfflineDownloading(null); }
  };

  const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0; let v = n;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  const runBackupNow = async () => {
    setConfirmOpen(false);
    setRunningNow(true);
    try {
      const { data } = await api.post("/admin/backups/run");
      if (data.status === "success") toast.success("Backup complete and uploaded");
      else if (data.status === "pending_cloud_upload") toast.warning("Local backup complete — cloud upload pending Drive setup");
      else toast.error(`Backup ${data.status}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Backup failed");
    } finally { setRunningNow(false); }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/backups/settings", {
        enabled:         !!settings.enabled,
        interval_days:   parseInt(settings.interval_days, 10) || 7,
        hour:            parseInt(settings.hour, 10),
        minute:          parseInt(settings.minute, 10),
        timezone:        settings.timezone || "Asia/Kolkata",
        drive_folder_id: settings.drive_folder_id || null,
      });
      setSettings({ ...data });
      toast.success("Automatic backup settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-500/10 border border-red-500/40 text-red-200 rounded-lg p-4 text-sm">
          Admin access only.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="backups-page">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase font-heading tracking-wider text-[#39FF14]">System · Maintenance</div>
        <h1 className="text-2xl font-heading font-bold text-[#F8F9FA]">Backups</h1>
        <p className="text-sm text-[#8A8F98] mt-1">
          On-demand and scheduled MongoDB backups with cloud verification. Restore is intentionally not available here.
        </p>
      </div>

      {/* Drive-configured banner */}
      {settings && !settings.drive_configured && (
        <div className="bg-yellow-500/10 border border-yellow-500/40 text-yellow-200 rounded-lg p-3 flex items-start gap-2" data-testid="drive-not-configured-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div className="text-xs">
            <strong className="font-heading">Google Drive credentials are not configured.</strong>{" "}
            Backups will run locally and be marked <em>pending_cloud_upload</em>. Configure a service account and set{" "}
            <code className="text-[#F8F9FA]">KOVA_GDRIVE_SERVICE_ACCOUNT_JSON</code> (or <code>KOVA_GDRIVE_SERVICE_ACCOUNT_FILE</code>) on the backend to enable cloud upload.
          </div>
        </div>
      )}

      {/* Offline (pod-local) backup — admin-only emergency path, independent of Google Drive. */}
      <div className="bg-[#121815] border border-[#39FF14]/30 rounded-lg p-5" data-testid="offline-backup-panel">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-[10px] uppercase font-heading tracking-wider text-[#39FF14] mb-1 flex items-center gap-1">
              <DatabaseBackup size={11} /> Offline snapshot (emergency)
            </div>
            <h2 className="text-lg font-heading font-bold text-[#F8F9FA]">Pod-local mongodump — one-click download</h2>
            <p className="text-xs text-[#8A8F98] mt-1 max-w-3xl">
              Runs <code className="text-[#F8F9FA]">mongodump</code> against the current production database and
              writes it to the pod's <code className="text-[#F8F9FA]">/app/backup/</code> directory. The download
              streams a <code>.tar.gz</code> directly to your computer — no Google Drive, no third-party upload.
              Backups are kept on the pod until you delete them manually.
            </p>
          </div>
          <Button onClick={runOfflineBackup} disabled={offlineRunning}
            className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#2ECC71] shrink-0"
            data-testid="offline-backup-run-btn">
            {offlineRunning ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Running…</>
                            : <><DatabaseBackup className="mr-2 h-4 w-4" /> Create offline backup</>}
          </Button>
        </div>

        {offlineRows.length === 0 ? (
          <div className="text-xs text-[#8A8F98] py-6 text-center border border-dashed border-[#3A4640] rounded"
               data-testid="offline-backup-empty">
            No pod-local backups yet. Click <strong>Create offline backup</strong> to produce one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] border-b border-[#3A4640]">
                  <th className="text-left px-2 py-2">Backup ID</th>
                  <th className="text-left px-2 py-2">Created (UTC)</th>
                  <th className="text-left px-2 py-2">DB</th>
                  <th className="text-right px-2 py-2">BSON files</th>
                  <th className="text-right px-2 py-2">Size</th>
                  <th className="text-right px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {offlineRows.map((b) => (
                  <tr key={b.backup_id} className="border-b border-[#3A4640] last:border-b-0"
                      data-testid={`offline-backup-row-${b.backup_id}`}>
                    <td className="px-2 py-2 font-mono text-xs text-[#F8F9FA]">{b.backup_id}</td>
                    <td className="px-2 py-2 text-xs text-[#8A8F98]">{formatDate(b.created_at) || b.created_at}</td>
                    <td className="px-2 py-2 text-xs text-[#8A8F98]">{b.db_name || "—"}</td>
                    <td className="px-2 py-2 text-xs text-[#F8F9FA] text-right">{b.bson_files || "—"}</td>
                    <td className="px-2 py-2 text-xs text-[#F8F9FA] text-right">{fmtBytes(b.size_bytes)}</td>
                    <td className="px-2 py-2 text-right">
                      <Button size="sm" variant="outline"
                        onClick={() => downloadOffline(b.backup_id)}
                        disabled={offlineDownloading === b.backup_id || !b.has_manifest}
                        data-testid={`offline-backup-download-${b.backup_id}`}>
                        {offlineDownloading === b.backup_id
                          ? <><Loader2 className="mr-1 h-3 w-3 animate-spin" /> Downloading…</>
                          : <><Download className="mr-1 h-3 w-3" /> Download</>}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[11px] text-[#8A8F98] mt-2">
              The archive is streamed on-the-fly — no second copy of the backup is written to the pod. Transfers may take several minutes depending on database size.
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5">
          <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-2">Back up now</div>
          <p className="text-sm text-[#8A8F98] mb-4">Immediately run a full backup. Streams into Google Drive when configured.</p>
          <Button onClick={() => setConfirmOpen(true)} disabled={runningNow} className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#2ECC71]" data-testid="backup-now-btn">
            {runningNow ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Backing up…</> : <><PlayCircle className="mr-2 h-4 w-4" /> Back up now</>}
          </Button>
        </div>

        {settings && (
          <div className="bg-[#121815] border border-[#3A4640] rounded-lg p-5" data-testid="auto-backup-config">
            <div className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] mb-2 flex items-center gap-2"><Clock size={11} /> Automatic backups</div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Enabled</Label>
                <Switch checked={!!settings.enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: !!v }))} data-testid="auto-backup-enabled" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px]">Every N days</Label>
                  <Input type="number" min={1} value={settings.interval_days ?? 7} onChange={(e) => setSettings((s) => ({ ...s, interval_days: e.target.value }))} data-testid="interval-days-input" />
                </div>
                <div>
                  <Label className="text-[11px]">Hour</Label>
                  <Input type="number" min={0} max={23} value={settings.hour ?? 2} onChange={(e) => setSettings((s) => ({ ...s, hour: e.target.value }))} data-testid="hour-input" />
                </div>
                <div>
                  <Label className="text-[11px]">Minute</Label>
                  <Input type="number" min={0} max={59} value={settings.minute ?? 0} onChange={(e) => setSettings((s) => ({ ...s, minute: e.target.value }))} data-testid="minute-input" />
                </div>
              </div>
              <div>
                <Label className="text-[11px]">Timezone</Label>
                <Input value={settings.timezone || "Asia/Kolkata"} onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))} data-testid="timezone-input" />
              </div>
              <div>
                <Label className="text-[11px]">Drive folder id (optional)</Label>
                <Input value={settings.drive_folder_id || ""} placeholder="Leave blank for KOVA/Backups"
                  onChange={(e) => setSettings((s) => ({ ...s, drive_folder_id: e.target.value }))}
                  data-testid="drive-folder-input" />
              </div>
              <Button onClick={saveSettings} className="w-full" data-testid="save-schedule-btn">Save schedule</Button>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden" data-testid="backup-history">
        <div className="p-4 border-b border-[#3A4640] flex items-center gap-2">
          <HardDrive size={16} className="text-[#39FF14]" />
          <div className="text-sm font-heading font-bold text-[#F8F9FA]">Backup history</div>
          <span className="text-[11px] text-[#8A8F98]">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F0C] text-[#8A8F98] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Backup ID</th>
              <th className="text-left px-4 py-2">Started</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">By</th>
              <th className="text-right px-4 py-2">Collections</th>
              <th className="text-right px-4 py-2">Documents</th>
              <th className="text-left px-4 py-2">Cloud</th>
              <th className="text-left px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-[#8A8F98]">No backups recorded yet. Click Back up now to create the first one.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#3A4640] hover:bg-[#0B0F0C]/50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`backup-row-${r.id}`}>
                <td className="px-4 py-2.5 font-mono text-[11px] text-[#F8F9FA]">{r.backup_id || "—"}</td>
                <td className="px-4 py-2.5 text-[#8A8F98]">{r.started_at ? formatDate(r.started_at) : "—"}</td>
                <td className="px-4 py-2.5">
                  <span className="text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border border-[#3A4640] bg-[#0B0F0C] text-[#F8F9FA]">{r.type}</span>
                </td>
                <td className="px-4 py-2.5 text-[#8A8F98]">{r.initiated_by}</td>
                <td className="px-4 py-2.5 text-right text-[#F8F9FA]">{r.collection_count ?? "—"}</td>
                <td className="px-4 py-2.5 text-right text-[#F8F9FA]">{r.total_documents ?? "—"}</td>
                <td className="px-4 py-2.5 text-[#8A8F98]">
                  {r.drive?.status === "uploaded" ? (
                    <span className="inline-flex items-center gap-1 text-[#39FF14]"><Cloud size={12} /> Uploaded</span>
                  ) : r.drive?.status === "not_configured" ? (
                    <span className="inline-flex items-center gap-1 text-yellow-300"><Cloud size={12} /> Not configured</span>
                  ) : r.drive?.status === "failed" ? (
                    <span className="inline-flex items-center gap-1 text-red-300"><Cloud size={12} /> Failed</span>
                  ) : (
                    <span className="text-[#8A8F98]">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE[r.status] || STATUS_BADGE.in_progress}`} data-testid={`status-${r.id}`}>
                    {r.status?.replace(/_/g, " ")}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="backup-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck size={16} /> Confirm manual backup</DialogTitle>
            <DialogDescription>
              This will run <code>mongodump</code> against the current database and upload to Google Drive when configured. The database itself is not modified.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={runBackupNow} className="bg-[#39FF14] text-[#0B0F0C] hover:bg-[#2ECC71]" data-testid="backup-confirm-btn">
              Yes, back up now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="backup-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Info size={16} /> Backup details</DialogTitle>
            <DialogDescription className="font-mono">{detail?.backup_id}</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type"          value={detail.type} />
                <Field label="Status"        value={detail.status} />
                <Field label="Initiated by"  value={detail.initiated_by} />
                <Field label="Started"       value={detail.started_at ? formatDate(detail.started_at) : "—"} />
                <Field label="Finished"      value={detail.finished_at ? formatDate(detail.finished_at) : "—"} />
                <Field label="Database"      value={detail.database_name} />
                <Field label="Collections"   value={detail.collection_count} />
                <Field label="Documents"     value={detail.total_documents} />
              </div>
              {detail.local_verification && (
                <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                  <div className="text-[10px] uppercase text-[#8A8F98] mb-1 flex items-center gap-1"><CheckCircle2 size={11} className="text-[#39FF14]" /> Local verification</div>
                  <pre className="text-[11px] text-[#F8F9FA] overflow-x-auto">{JSON.stringify(detail.local_verification, null, 2)}</pre>
                </div>
              )}
              {detail.cloud_verification && (
                <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                  <div className="text-[10px] uppercase text-[#8A8F98] mb-1 flex items-center gap-1"><Cloud size={11} className="text-[#39FF14]" /> Cloud verification</div>
                  <pre className="text-[11px] text-[#F8F9FA] overflow-x-auto">{JSON.stringify(detail.cloud_verification, null, 2)}</pre>
                </div>
              )}
              {detail.drive && (
                <div className="p-3 bg-[#0B0F0C] border border-[#3A4640] rounded">
                  <div className="text-[10px] uppercase text-[#8A8F98] mb-1">Drive destination</div>
                  <div className="text-[11px] text-[#F8F9FA] break-all">{detail.drive.folder_path || detail.drive.detail || "—"}</div>
                </div>
              )}
              {detail.error && (
                <div className="p-3 bg-red-500/10 border border-red-500/40 rounded">
                  <div className="text-[10px] uppercase text-red-300 mb-1">Error</div>
                  <pre className="text-[11px] text-red-200 whitespace-pre-wrap overflow-x-auto">{JSON.stringify(detail.error, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="p-2 bg-[#0B0F0C] border border-[#3A4640] rounded">
      <div className="text-[10px] uppercase text-[#8A8F98]">{label}</div>
      <div className="text-sm text-[#F8F9FA] font-heading">{value ?? "—"}</div>
    </div>
  );
}
